/**
 * The speakers (D192): every voice a line speaks in is one the pack declared,
 * and every voice the pack declared is one some line speaks in.
 *
 * @remarks
 * A line's `source` is the engine's fixed four or a declared speaker id; the
 * schema can only say "a content id", so the cross-reference is the loader's
 * (the D47 family: a reference to nothing). Two failures and one warning:
 *
 * - `source` naming no declared speaker — a hard failure at the line.
 * - two declarations of one `speakerId` — a hard failure at the second.
 * - a declared speaker no line speaks in — a warning (`speaker_silent`): a
 *   channel with an index and a quotation law that nothing uses is a thing
 *   shipped that nothing can reach.
 *
 * The walk is STRUCTURAL over the parsed pack — any object carrying a
 * `source` string — because a line may sit in a room, an item, a reaction, a
 * voice pool, a paywall, an ending or an override, and a reader that listed
 * those places would miss the next one (D136's lesson).
 *
 * @packageDocumentation
 */

import { type ContentPackDefinition, isFixedTranscriptSource, type SpeakerId } from '@orpheus/content-schema';

import type { ReferenceBrokenFailure } from './load-failure.js';

import { brokenReference, collectDuplicates } from './semantic-validation.js';

/** A declared speaker no line speaks in (D192). */
export interface SpeakerSilent {
	readonly speakerId: SpeakerId;
}

/** One `source` seen on the walk: the id and where it sits. */
interface SourceUse {
	readonly path: string;
	readonly source: string;
}

/**
 * Every `source` a node's lines carry, wherever they sit, with paths.
 *
 * @param node - Any node BELOW the pack root — the root is the pack itself, never a line, so a path is always in hand
 * @param path - Where the node sits, for the report
 * @param found - Accumulator
 * @returns The accumulator
 */
const collectSourceUses = (node: unknown, path: string, found: SourceUse[]): SourceUse[] => {
	if (Array.isArray(node)) {
		node.forEach((element, index) => collectSourceUses(element, `${path}[${String(index)}]`, found));

		return found;
	}

	if (typeof node !== 'object' || node === null) {
		return found;
	}

	const record = node as Readonly<Record<string, unknown>>;
	const source = record['source'];

	if (typeof source === 'string') {
		found.push({ path: `${path}.source`, source });
	}

	for (const [key, value] of Object.entries(record)) {
		collectSourceUses(value, `${path}.${key}`, found);
	}

	return found;
};

/**
 * Every `source` a pack's lines carry, in walk order.
 *
 * @param contentPack - The parsed pack
 * @returns The uses, each with its path from the pack root
 */
const sourceUsesOf = (contentPack: ContentPackDefinition): readonly SourceUse[] =>
	Object.entries(contentPack).flatMap(([key, value]) => collectSourceUses(value, key, []));

/**
 * Validate the speakers' authoring rules.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure, in walk order
 */
export const validateSpeakerAuthoring = (contentPack: ContentPackDefinition): ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const declared = collectDuplicates(
		contentPack.speakers.map((speaker, index) => ({ declarationPath: `speakers[${String(index)}].speakerId`, declaredId: speaker.speakerId })),
		'speaker',
		failures,
	);

	for (const use of sourceUsesOf(contentPack)) {
		if (!isFixedTranscriptSource(use.source as SpeakerId) && !declared.has(use.source)) {
			failures.push(
				brokenReference(
					use.path,
					use.source,
					`source "${use.source}" names no declared speaker — declare it under \`speakers\`, or speak the line in one of the engine's fixed sources (environment, human_log, player, system)`,
				),
			);
		}
	}

	return failures;
};

/**
 * Every declared speaker no line speaks in.
 *
 * @param contentPack - The parsed pack
 * @returns The silent speakers, in declaration order
 */
export const findSilentSpeakers = (contentPack: ContentPackDefinition): readonly SpeakerSilent[] => {
	// The speakers table itself carries no `source`, so the walk over the
	// whole pack counts only lines.
	const spoken = new Set(sourceUsesOf(contentPack).map((use) => use.source));

	return contentPack.speakers.filter((speaker) => !spoken.has(speaker.speakerId)).map((speaker) => ({ speakerId: speaker.speakerId }));
};
